import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Circle, Loader2, ArrowRight, Clock, MessagesSquare, ExternalLink, Lock } from "lucide-react";
import { toast, Toaster } from "sonner";
import { ASSETS } from "@/lib/constants";
import PhaseACard from "@/components/PhaseACard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "grc_bdm_onboarding_done";

export default function PartnerOnboarding() {
  const [params] = useSearchParams();
  const slug = params.get("mlo") || "";
  const token = params.get("k") || "";
  const [track, setTrack] = useState(null);
  const [mlo, setMlo] = useState(null);
  const [owner, setOwner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; }
  });

  useEffect(() => {
    axios
      .get(`${API}/onboarding/bdm${token ? `?k=${encodeURIComponent(token)}` : ""}`)
      .then((r) => {
        setTrack(r.data);
        if (r.data?.progress) {
          setOwner(r.data.progress);
          setDone(r.data.progress.steps_done || []);
        }
      })
      .catch(() => setTrack({ steps: [] }));
  }, [token]);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/mlos/${slug}/public`).then((r) => setMlo(r.data)).catch(() => setMlo(null));
  }, [slug]);

  const toggle = async (key) => {
    if (locked) return;
    const next = done.includes(key) ? done.filter((k) => k !== key) : [...done, key];
    setDone(next);
    if (!owner) {
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/onboarding/bdm/progress`, {
        token,
        key,
        done: next.includes(key),
      });
      setDone(data.steps_done || next);
    } catch {
      setDone(done);
      toast.error("Couldn't save that — check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const steps = useMemo(() => track?.steps || [], [track]);
  const phase = track?.phase || null;
  const welcome = track?.welcome_video || null;
  const locked = Boolean(phase && !phase.approved);
  const community = track?.community;
  const doneCount = useMemo(() => steps.filter((s) => done.includes(s.key)).length, [steps, done]);
  const percent = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;

  if (!track) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linen text-navy antialiased" data-testid="partner-onboarding-page">
      <header className="bg-white border-b border-linen-border">
        <div className="container-grc h-16 flex items-center justify-between">
          <Link to="/"><img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" /></Link>
          <a href="/partner" className="text-sm text-teal hover:underline">The partner program</a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(115deg,#08192F 0%,#0F2A47 45%,#17385e 78%,#1F8F8B 160%)" }}>
        <div className="container-grc py-14 sm:py-20 max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#A6D24A]">Partner onboarding · BDM track</p>
          <h1 className="mt-3 font-heading font-medium text-4xl sm:text-5xl text-white leading-[1.08]">
            {locked ? (
              <>Becoming a partner,<br />in three short steps.</>
            ) : (
              <>Your first week,<br />in {steps.length} short steps.</>
            )}
          </h1>
          <p className="mt-5 text-base text-white/80 leading-relaxed max-w-xl">
            {locked
              ? "You've done the part that matters — the application and the commitments are signed. Here's exactly where you stand, updated as we review it. Nothing else is needed from you."
              : "No binder, no training course. Work down this list in order and you'll be fully operational before your first monthly meeting. Most people finish it in a single afternoon."}
          </p>
          {!locked && (
            <div className="mt-8 flex items-center gap-4">
              <div className="flex-1 max-w-xs h-2 rounded-full bg-white/15 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#A6D24A]"
                  style={{ width: `${percent}%`, transition: "width .6s cubic-bezier(.22,1,.36,1)" }}
                  data-testid="partner-onboarding-progress"
                />
              </div>
              <span className="text-sm text-white/80 tabular-nums">{doneCount} of {steps.length} done</span>
            </div>
          )}
          {owner?.name && !locked && (
            <p className="mt-6 text-sm text-white/65" data-testid="partner-onboarding-owner">
              This checklist is <span className="text-white font-medium">{owner.name}</span>'s — your progress
              saves automatically, so you can stop halfway and come back.
              {saving ? <span className="ml-2 text-white/40">Saving…</span> : null}
            </p>
          )}
          {mlo?.name && (
            <p className="mt-6 text-sm text-white/65" data-testid="partner-onboarding-sponsor">
              Your loan officer is <span className="text-white font-medium">{mlo.name}</span>
              {mlo.phone ? ` · ${mlo.phone}` : ""}. Any question on this page, ask them first.
            </p>
          )}
        </div>
      </section>

      {/* Steps */}
      <main className="container-grc py-12 sm:py-16 max-w-3xl">
        {welcome && (
          <section className="mb-10 rounded-3xl bg-white border border-linen-border shadow-soft overflow-hidden" data-testid="welcome-video-block">
            <div className="px-6 sm:px-8 pt-6">
              <p className="text-[11px] uppercase tracking-[0.2em] text-teal font-bold">Sixty seconds first</p>
              <h2 className="mt-1.5 font-heading font-medium text-2xl text-navy">
                {welcome.from_name ? `A word from ${welcome.from_name}.` : "A quick welcome."}
              </h2>
            </div>
            <div className="mt-5 bg-navy">
              {welcome.kind === "file" ? (
                <video
                  src={`${process.env.REACT_APP_BACKEND_URL}${welcome.url}`}
                  controls
                  data-testid="welcome-video-player"
                  className="w-full max-h-[420px] bg-black"
                />
              ) : (
                <iframe
                  src={welcome.embed}
                  title="Partner welcome"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  allowFullScreen
                  data-testid="welcome-video-embed"
                  className="w-full aspect-video"
                />
              )}
            </div>
          </section>
        )}

        {phase && (
          <div className="mb-10">
            <PhaseACard phase={phase} />
          </div>
        )}

        {locked && (
          <div
            className="mb-6 rounded-2xl border border-linen-border bg-white px-5 py-4 flex items-start gap-3"
            data-testid="first-week-locked"
          >
            <Lock size={16} className="shrink-0 mt-0.5 text-ink-400" />
            <p className="text-sm text-ink-600 leading-relaxed">
              <strong className="text-navy">Your first week is next.</strong> These {steps.length} steps
              unlock the moment you're approved — starting with registering at NEXA, which is what makes
              you payable. Have a read now if you like; you just can't tick anything off yet.
            </p>
          </div>
        )}

        <ol className={`space-y-4 ${locked ? "opacity-60" : ""}`} data-testid="partner-onboarding-steps">
          {steps.map((s, i) => {
            const isDone = done.includes(s.key);
            const links = s.key === "community" && community?.enabled
              ? [{ label: `Join the ${community.platform_label || "group"}`, url: community.url, external: true }]
              : (s.links || []).map((l) => ({ label: l.label, url: l.path, external: false }));
            return (
              <li
                key={s.key}
                className={`rounded-3xl border bg-white p-6 sm:p-7 transition-all ${isDone ? "border-teal/40 bg-teal-light/20" : "border-linen-border shadow-soft"}`}
                data-testid={`partner-step-${s.key}`}
              >
                <div className="flex items-start gap-4">
                  <button
                    onClick={() => toggle(s.key)}
                    disabled={locked}
                    data-testid={`partner-step-toggle-${s.key}`}
                    aria-label={isDone ? `Mark ${s.label} not done` : `Mark ${s.label} done`}
                    className={`shrink-0 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center border transition-all ${
                      isDone ? "bg-teal border-teal text-white" : "border-ink-300 text-ink-300 hover:border-teal hover:text-teal"
                    } ${locked ? "cursor-not-allowed" : ""}`}
                  >
                    {isDone ? <Check size={15} strokeWidth={3} /> : <Circle size={8} fill="currentColor" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="text-[11px] uppercase tracking-widest text-teal font-bold">Step {i + 1}</span>
                      {s.time && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-ink-400 bg-linen rounded-full px-2 py-0.5">
                          <Clock size={10} /> {s.time}
                        </span>
                      )}
                    </div>
                    <h2 className={`mt-1.5 font-heading font-medium text-xl sm:text-2xl ${isDone ? "text-ink-400" : "text-navy"}`}>
                      {s.label}
                    </h2>
                    <p className="mt-2 text-[15px] text-ink-600 leading-relaxed">{s.why}</p>
                    {links.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {links.map((l) => (
                          <a
                            key={l.url}
                            href={l.url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`partner-step-link-${s.key}`}
                            className="inline-flex items-center gap-1.5 text-[13px] font-semibold bg-navy text-white px-4 py-2 rounded-full hover:bg-[#173a5f] transition-colors"
                          >
                            {l.label} {l.external ? <ExternalLink size={12} /> : <ArrowRight size={13} />}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>

        {community?.enabled && (
          <div className="mt-10 rounded-3xl p-7 text-white" style={{ background: "linear-gradient(140deg,#0F2A47 0%,#1F8F8B 180%)" }} data-testid="partner-onboarding-community">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#A6D24A]">
              <MessagesSquare size={13} /> Stuck on any of this?
            </div>
            <h3 className="mt-1 font-heading font-medium text-2xl">Ask in {community.label}</h3>
            <p className="mt-2 text-sm text-white/75 max-w-lg">{community.note}</p>
            <a
              href={community.url}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#A6D24A] text-[#08192F] font-semibold text-sm px-5 py-2.5 hover:brightness-105 transition-all"
            >
              Join the group <ArrowRight size={14} />
            </a>
          </div>
        )}

        <p className="mt-10 text-center text-xs text-ink-400">
          {owner
            ? "Your progress is saved to your account — open this link on any device and it picks up where you left off."
            : "Your progress is saved on this device. Bookmark this page and come back to it."}
        </p>
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
